import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, ChevronDown, Mail, MessageCircle, Send } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { driver, useAction } from '@/api/driver'
import { isMissing, support, type SupportKind, type SupportThread } from '@/api/proposed'
import { EmptyState, LoadingBlock, PageTitle, StatusPill } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Field, Input, Select, Textarea } from '@/components/ui/form'
import { useAuth } from '@/lib/auth'
import { ago, dateTime, npr } from '@/lib/format'
import { useI18n } from '@/lib/i18n'
import { cn, errorText } from '@/lib/utils'

const FAQ: [string, string][] = [
  ['How do I start charging?', 'Find a free connector on the map, open the station, press Start, choose the card to pay with and plug in. You can stop from the Charging tab.'],
  ['Why do I need a card?', 'Every charge is paid from a wallet linked to an RFID card. Ask the staff at any station for a card; it is linked to your phone number.'],
  ['How do I add credit?', 'Pay at the counter of any station and the staff add it to your wallet with that operator. Online payment with eSewa, Khalti and Fonepay is coming soon.'],
  ['The charger did not start', 'Check the cable is fully in and the car is unlocked, then try again. If it still fails, use Report a problem on the station page so the team can check the charger.'],
  ['I was charged the wrong amount', 'Open the receipt in History, then send us a message below with the receipt number. Refunds go back to the wallet that paid.'],
  ['Can I use one card at every station?', 'An operator card works at that operator’s stations. A Saadhan super card works at every station on Saadhan Charge.'],
]

const KINDS: { id: SupportKind; label: string }[] = [
  { id: 'payment', label: 'Payment or refund' },
  { id: 'problem', label: 'Problem at a charger' },
  { id: 'account', label: 'My account' },
  { id: 'other', label: 'Something else' },
]
const STATUS: Record<SupportThread['status'], { label: string; tone: 'warning' | 'info' | 'success' }> = {
  open: { label: 'Sent', tone: 'warning' },
  waiting: { label: 'Replied', tone: 'info' },
  resolved: { label: 'Resolved', tone: 'success' },
}

export function HelpPage() {
  const { t } = useI18n()
  const { user } = useAuth()
  const [faq, setFaq] = useState<number | null>(0)
  const threads = useQuery({ queryKey: ['support'], queryFn: support.list, enabled: !!user, retry: false, refetchInterval: 60_000 })
  const available = !!user && !!threads.data
  const [openId, setOpenId] = useState<number | null>(null)
  const [writing, setWriting] = useState(false)

  if (openId != null) return <Thread id={openId} onBack={() => setOpenId(null)} />
  if (writing) return <NewMessage onDone={(id) => { setWriting(false); if (id) setOpenId(id) }} />

  return (
    <div>
      <PageTitle title={t('Help')} sub={t('Answers to common questions, and a way to reach the station team.')} />
      {available && (
        <>
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-text-2">{t('Your messages')}</h2>
            <Button size="sm" onClick={() => setWriting(true)}><MessageCircle /> {t('New message')}</Button>
          </div>
          {threads.data!.threads.length ? (
            <Card className="mb-6 divide-y divide-border">
              {threads.data!.threads.map((th) => (
                <button key={th.id} onClick={() => setOpenId(th.id)} className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-surface-2">
                  <div className="min-w-0 flex-1">
                    <p className={cn('truncate text-sm', th.unread ? 'font-semibold' : 'font-medium')}>{th.subject}</p>
                    <p className="text-xs text-text-2">{[th.station_name, ago(th.updated_at)].filter(Boolean).join(' · ')}</p>
                  </div>
                  {th.unread && <span className="size-2 rounded-full bg-brand" />}
                  <StatusPill tone={STATUS[th.status].tone}>{t(STATUS[th.status].label)}</StatusPill>
                </button>
              ))}
            </Card>
          ) : (
            <Card className="mb-6 p-4 text-sm text-text-2">{t('No messages yet.')}</Card>
          )}
        </>
      )}

      <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-text-2">{t('Common questions')}</h2>
      <Card className="divide-y divide-border">
        {FAQ.map(([q, a], i) => (
          <div key={q}>
            <button onClick={() => setFaq(faq === i ? null : i)} aria-expanded={faq === i} className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm font-medium">
              {t(q)} <ChevronDown className={cn('size-4 shrink-0 text-muted transition-transform', faq === i && 'rotate-180')} />
            </button>
            {faq === i && <p className="px-4 pb-4 text-sm text-text-2">{t(a)}</p>}
          </div>
        ))}
      </Card>

      {!available && (
        <Card className="mt-6 p-4">
          <p className="font-semibold">{t('Still need help?')}</p>
          <p className="mt-1 text-sm text-text-2">{user || threads.isLoading ? t('Call the station from its page on the map, or write to us.') : <>{t('Call the station from its page on the map, or write to us.')} <Link to="/login?next=/help" className="underline">{t('Sign in')}</Link></>}</p>
          <a href="mailto:summit@saadhan.app?subject=Saadhan%20Charge%20help" className="mt-3 flex h-11 items-center justify-center gap-2 rounded-lg bg-primary text-sm font-medium text-primary-fg"><Mail className="size-4" /> summit@saadhan.app</a>
        </Card>
      )}
    </div>
  )
}

function NewMessage({ onDone }: { onDone: (id?: number) => void }) {
  const { t } = useI18n()
  const recent = useQuery({ queryKey: ['history', 'recent'], queryFn: () => driver.history({ page: 1, per_page: 10 }) })
  const [kind, setKind] = useState<SupportKind>('payment')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [session, setSession] = useState<number | ''>('')
  const s = recent.data?.sessions.find((x) => x.id === session)
  const send = useAction(() => support.create({ kind, subject: subject.trim() || t(KINDS.find((k) => k.id === kind)!.label), message: message.trim(), session_id: session || undefined, station_id: s?.station_id ?? undefined, charger_id: s?.charger_id, connector_number: s?.connector_id }), {
    quietError: true,
    success: t('Message sent. You will see the reply here.'),
    invalidate: [['support']],
    onSuccess: (r) => onDone(r.id),
  })
  return (
    <div className="mx-auto max-w-lg">
      <button onClick={() => onDone()} className="-ml-2 mb-3 flex items-center gap-1 rounded-lg px-2 py-1 text-sm text-text-2 hover:bg-surface-3"><ArrowLeft className="size-4" /> {t('Help')}</button>
      <PageTitle title={t('New message')} />
      <Card className="grid gap-4 p-4">
        <Field label={t('About')}>{(p) => <Select {...p} value={kind} onChange={(e) => setKind(e.target.value as SupportKind)}>{KINDS.map((k) => <option key={k.id} value={k.id}>{t(k.label)}</option>)}</Select>}</Field>
        <Field label={t('Which charge? (optional)')}>
          {(p) => (
            <Select {...p} value={session} onChange={(e) => setSession(e.target.value ? Number(e.target.value) : '')}>
              <option value="">{t('Not about a charge')}</option>
              {recent.data?.sessions.map((x) => <option key={x.id} value={x.id}>{`${dateTime(x.start_time)} · ${x.station_name ?? x.charger_id} · ${npr(x.cost)}`}</option>)}
            </Select>
          )}
        </Field>
        <Field label={t('Subject')}>{(p) => <Input {...p} value={subject} onChange={(e) => setSubject(e.target.value)} />}</Field>
        <Field label={t('Message')}>{(p) => <Textarea {...p} className="min-h-28" value={message} onChange={(e) => setMessage(e.target.value)} />}</Field>
        <Button loading={send.isPending} disabled={!message.trim()} onClick={() => send.mutate(undefined, { onError: (e) => toast.error(isMissing(e) ? t('Messages are coming soon. Please email summit@saadhan.app.') : errorText(e)) })}><Send /> {t('Send')}</Button>
      </Card>
    </div>
  )
}

function Thread({ id, onBack }: { id: number; onBack: () => void }) {
  const { t } = useI18n()
  const q = useQuery({ queryKey: ['support', id], queryFn: () => support.get(id), refetchInterval: 20_000 })
  const [body, setBody] = useState('')
  const reply = useAction(() => support.reply(id, body.trim()), { invalidate: [['support']], onSuccess: () => setBody('') })
  return (
    <div className="mx-auto max-w-lg">
      <button onClick={onBack} className="-ml-2 mb-3 flex items-center gap-1 rounded-lg px-2 py-1 text-sm text-text-2 hover:bg-surface-3"><ArrowLeft className="size-4" /> {t('Help')}</button>
      {q.isLoading ? (
        <LoadingBlock rows={4} />
      ) : !q.data ? (
        <EmptyState title={t('Message not found')} />
      ) : (
        <>
          <PageTitle title={q.data.subject} sub={[q.data.station_name, q.data.session_id ? t('Receipt #{id}', { id: q.data.session_id }) : null].filter(Boolean).join(' · ')} />
          <div className="space-y-3">
            {q.data.messages?.map((m) => (
              <div key={m.id} className={cn('max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm', m.from === 'driver' ? 'ml-auto bg-ink text-white' : 'bg-surface border border-border')}>
                <p className="whitespace-pre-wrap">{m.body}</p>
                {m.photo_urls.length > 0 && <div className="mt-2 flex flex-wrap gap-2">{m.photo_urls.map((u) => <img key={u} src={u} alt="" className="h-20 rounded-lg object-cover" />)}</div>}
                <p className={cn('mt-1 text-[11px]', m.from === 'driver' ? 'text-white/60' : 'text-text-2')}>{m.from === 'staff' ? m.author_name ?? t('Station team') : t('You')} · {dateTime(m.created_at)}</p>
              </div>
            ))}
          </div>
          {q.data.status !== 'resolved' && (
            <Card className="mt-4 p-3">
              <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder={t('Write a reply')} aria-label={t('Reply')} />
              <div className="mt-2 flex justify-end"><Button size="sm" loading={reply.isPending} disabled={!body.trim()} onClick={() => reply.mutate(undefined)}><Send /> {t('Send')}</Button></div>
            </Card>
          )}
        </>
      )}
    </div>
  )
}
